(() => {
  if (window.TFFCProductReviews) return;
  const store = 'www.amaranthineblooms.co.uk';
  const cacheKey = 'tffc-product-reviews-v1';
  const lifetime = 15 * 60 * 1000;
  const requests = new Map();
  const decisions = new Map();
  const numbers = new Map();
  const normaliseSkus = skus => [...new Set(skus.map(sku => String(sku ?? '').trim()).filter(Boolean))].sort().join(';');

  function stats(rating, count) {
    if (!['number', 'string'].includes(typeof rating) || !['number', 'string'].includes(typeof count) || String(rating).trim() === '' || String(count).trim() === '') return null;
    rating = Number(rating);
    count = Number(count);
    if (!Number.isInteger(count) || count < 0 || !Number.isFinite(rating) || rating < 0 || rating > 5 || count > 0 && rating < 1) return null;
    return { rating, count };
  }

  function cached(key) {
    const entry = numbers.get(key);
    return entry && entry.time <= Date.now() && Date.now() - entry.time < lifetime && stats(entry.rating, entry.count) ? stats(entry.rating, entry.count) : null;
  }

  // Storage is optional. Keep bounded numeric summaries only; never store review bodies.
  try {
    const saved = JSON.parse(sessionStorage.getItem(cacheKey));
    if (saved?.store === store && Array.isArray(saved.entries)) {
      saved.entries.slice(-50).forEach(([key, value]) => {
        if (typeof key === 'string' && value && Number.isFinite(value.time)) numbers.set(key, value);
      });
    }
  } catch (_) { /* Private browsing, corrupt data or disabled storage. */ }

  function remember(key, value) {
    numbers.delete(key);
    numbers.set(key, { ...value, time: Date.now() });
    for (const key of numbers.keys()) if (!cached(key)) numbers.delete(key);
    while (numbers.size > 50) numbers.delete(numbers.keys().next().value);
    try { sessionStorage.setItem(cacheKey, JSON.stringify({ store, entries: [...numbers] })); } catch (_) { /* Memory cache still works. */ }
  }

  function request(sku, revalidate = false) {
    const key = sku || '*';
    const hit = cached(key);
    if (hit && !revalidate) return Promise.resolve(hit);
    if (requests.has(key)) return requests.get(key);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    const params = new URLSearchParams({ store });
    if (sku) { params.set('type', 'product_review'); params.set('sku', sku); params.set('per_page', '1'); }
    const promise = fetch(`https://api.reviews.io/${sku ? 'reviews' : 'stats/all'}?${params}`, {
      credentials: 'omit', referrerPolicy: 'no-referrer', signal: controller.signal
    }).then(response => {
      if (!response.ok) throw new Error('Reviews unavailable');
      return response.json();
    }).then(data => {
      const value = sku ? stats(data.average_rating, data.review_count) : stats(data.product_review_stats?.average_rating, data.product_review_stats?.total_reviews);
      if (!value) throw new Error('Invalid review summary');
      remember(key, value);
      return value;
    }).catch(() => null).finally(() => { clearTimeout(timer); requests.delete(key); });
    requests.set(key, promise);
    return promise;
  }

  const minimumCount = Number(window.themeVariables?.settings?.productReviewsMinCount) || 10;
  const minimumScore = Number(window.themeVariables?.settings?.productReviewsMinScore) || 4.5;
  const qualifies = value => !!value && value.count >= minimumCount && value.rating >= minimumScore;
  const productStats = skus => { const sku = normaliseSkus(skus); return sku ? request(sku) : Promise.resolve(null); };

  function resolve(skus) {
    const sku = normaliseSkus(skus);
    if (!decisions.has(sku)) {
      decisions.set(sku, (async () => {
        const product = sku ? await request(sku, true) : null;
        if (qualifies(product)) return Object.freeze({ ...product, scope: 'product', sku });
        let overall = window.TFFCReviewsStats ? await window.TFFCReviewsStats.get('product') : await request('');
        if (overall?.cached) overall = await window.TFFCReviewsStats.get();
        // A failed request is not a zero rating. Both consumers keep the same fallback.
        return Object.freeze({ ...(overall?.count > 0 ? overall : {}), scope: overall?.scope === 'combined' ? 'combined' : 'all', sku: '', unavailable: !overall || !!overall.cached });
      })());
    }
    return decisions.get(sku);
  }

  window.TFFCProductReviews = Object.freeze({ store, resolve, normaliseSkus, productStats, qualifies });
})();

// Managed elements survive Shopify section replacement without repeating requests.
(() => {
  if (!window.customElements || customElements.get('product-review-summary')) return;
  const api = window.TFFCProductReviews;
  let hostSequence = 0;
  const skus = element => {
    try { const value = JSON.parse(element.dataset.skus); return Array.isArray(value) ? value : []; } catch (_) { return []; }
  };
  const scopeText = (element, scope) => element.dataset[scope === 'product' ? 'scopeProduct' : scope === 'combined' ? 'scopeCombined' : 'scopeAll'];

  class ProductReviewSummary extends ManagedHTMLElement {
    connectedCallback() {
      const generation = this.generation = (this.generation || 0) + 1;
      const link = this.querySelector('[data-review-link]');
      const widget = () => [...document.querySelectorAll('product-review-widget')].find(element => element.dataset.productId === this.dataset.productId);
      if (!this.hasAttribute('data-custom-link') && widget()) link.setAttribute('href', '#' + widget().id);
      const prepare = () => { if (!this.hasAttribute('data-custom-link')) widget()?.activate(); };
      for (const event of ['pointerenter', 'pointerdown', 'focusin']) this.listenTo(link, event, prepare, { passive: true });
      this.listenTo(link, 'click', event => {
        if (!link.hasAttribute('href')) { event.preventDefault(); return; }
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || this.hasAttribute('data-custom-link')) return;
        const target = widget();
        if (!target) return;
        event.preventDefault();
        target.activate();
        const anchor = target.closest('.reviews-io-widget')?.querySelector('.section__header') || target;
        anchor.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        target.focus({ preventScroll: true });
      });
      const render = value => {
        if (!this.isConnected || this.generation !== generation || !value?.count) return;
        this.dataset.scope = value.scope;
        const locale = document.documentElement.lang || 'en';
        const rating = Number(value.rating.toFixed(2));
        const displayRating = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rating);
        const count = new Intl.NumberFormat(locale).format(value.count);
        const stars = this.querySelector('[data-review-stars]');
        stars.hidden = false;
        stars.setAttribute('aria-label', this.dataset.ratingLabel.replace('__rating__', displayRating));
        this.querySelectorAll('[data-review-star]').forEach((star, index) => star.style.setProperty('--star-fill', `${Math.round(Math.min(1, Math.max(0, rating - index)) * 100)}%`));
        const score = this.querySelector('[data-review-score]');
        score.textContent = displayRating;
        score.hidden = false;
        const from = this.querySelector('[data-review-from]');
        from.textContent = this.dataset.from;
        from.hidden = false;
        this.querySelector('[data-review-count]').textContent = this.dataset[value.count === 1 ? 'countOne' : 'countOther'].replace('__count__', count);
        const label = this.querySelector('[data-review-scope]');
        label.textContent = scopeText(this, value.scope);
        label.hidden = false;
      };
      const href = link.getAttribute('href');
      let available = true;
      const unlink = () => { link.removeAttribute('href'); link.removeAttribute('role'); link.removeAttribute('tabindex'); };
      const fallback = async () => {
        available = false;
        unlink();
        const value = await window.TFFCReviewsStats?.get();
        if (!available) render(value && { ...value, scope: value.scope === 'combined' ? 'combined' : 'all' });
      };
      this.listenTo(document, 'reviews:availability', event => {
        if (event.detail.productId !== this.dataset.productId) return;
        if (!event.detail.available) fallback();
        else {
          available = true;
          link.setAttribute('href', href);
          api.resolve(skus(this)).then(value => { if (available) render(value); });
        }
      });
      const initial = window.TFFCReviewsStats?.peek();
      if (initial) { unlink(); render({ ...initial, scope: initial.scope === 'combined' ? 'combined' : 'all' }); }
      api.resolve(skus(this)).then(value => {
        if (value.unavailable) fallback();
        else if (available) { link.setAttribute('href', href); render(value); }
      });
    }
    disconnectedCallback() { super.disconnectedCallback(); this.generation++; }
  }

  class ProductReviewWidget extends ManagedHTMLElement {
    connectedCallback() {
      const generation = this.generation = (this.generation || 0) + 1;
      this.ready = api.resolve(skus(this));
      this.dataset.enhanced = '';
      this.ready.then(value => {
        if (!this.isConnected || this.generation !== generation) return;
        this.setAttribute('aria-label', scopeText(this, value.scope));
      });
      this.listenTo(document, 'visitorConsentCollected', () => { if (this.wanted) this.activate(); });
      this.listenTo(window, 'load', () => { if (this.wanted) this.activate(); });
      this.listenTo(document, 'visibilitychange', () => { if (!document.hidden) this.prepareInBackground(); });
      if (window.Shopify?.designMode) this.listenTo(document, 'shopify:section:select', event => { if (event.target.contains(this)) this.activate(); });
      this.observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) this.activate(); }, { rootMargin: '400px' });
      this.observer.observe(this);
      if (location.hash === '#' + this.id) this.activate();
      this.prepareInBackground();
    }

    async prepareInBackground() {
      const connection = navigator.connection;
      if (this.backgroundScheduled || this.mounted || document.hidden || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
      this.backgroundScheduled = true;
      const generation = this.generation;
      const hero = document.querySelector('product-media img[fetchpriority="high"]');
      if (hero) { try { await hero.decode(); } catch (_) { /* An unavailable image must not disable reviews. */ } }
      if (!this.isConnected || this.generation !== generation) return;
      // Allow the critical product image to paint before scheduling vendor work.
      this.requestFrame(() => this.requestFrame(() => {
        const start = () => {
          this.backgroundTask = null;
          if (!this.isConnected || this.generation !== generation) return;
          if (document.hidden) { this.backgroundScheduled = false; return; }
          this.activate();
        };
        this.backgroundTask = window.requestIdleCallback ? requestIdleCallback(start, { timeout: 1500 }) : setTimeout(start, 150);
      }));
    }

    async activate() {
      this.wanted = true;
      // Public numeric requests contain no tracking cookies. Polaris remains subject to
      // Shopify/Pandectes consent and the existing script blocker; never force execution.
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === false) {
        this.resetWidget();
        this.availability(false);
        this.querySelector('[data-review-fallback]').hidden = false;
        return;
      }
      if (this.loading || this.mounted || !this.isConnected) return;
      this.loading = true;
      this.setLoading(true);
      const generation = this.generation;
      try {
        const scope = await this.ready;
        if (!this.isConnected || this.generation !== generation) return;
        if (scope.unavailable) { this.availability(false); this.setLoading(false); return; }
        LibraryLoader.libraries.productReviewsWidget ||= { tagId: 'product-reviews-widget', src: this.dataset.widgetAsset, type: 'script' };
        LibraryLoader.libraries.reviewsPolaris ||= { tagId: 'reviews-polaris', src: 'https://widget.reviews.io/polaris/build.js', type: 'script' };
        LibraryLoader.libraries.reviewsStyles ||= { tagId: 'reviews-styles', src: 'https://assets.reviews.io/css/widgets/elements-widget.css?version=200', type: 'link' };
        LibraryLoader.libraries.reviewsIcons ||= { tagId: 'reviews-icons', src: 'https://assets.reviews.io/iconfont/reviewsio-icons/style.css?version=200', type: 'link' };
        await Promise.all(['productReviewsWidget', 'reviewsStyles', 'reviewsIcons'].map(name => LibraryLoader.load(name)).concat(typeof window.ReviewsWidget === 'function' ? [] : [LibraryLoader.load('reviewsPolaris')]));
        if (!this.isConnected || this.generation !== generation || window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === false) return;
        this.dataset.scope = scope.scope;
        const host = this.querySelector('[data-review-host]');
        host.id = `ReviewsWidget-${++hostSequence}`;
        this.dispose = window.TFFCReviewWidget(host, scope, () => {
          if (this.isConnected && this.generation === generation) {
            this.dataset.loaded = '';
            this.availability(true);
            this.setLoading(false);
            this.querySelector('[data-review-placeholder]').hidden = true;
          }
        }, () => {
          if (this.isConnected && this.generation === generation) { this.resetWidget(); this.availability(false); }
        });
        this.mounted = true;
        this.observer.disconnect();
      } catch (_) { if (this.isConnected && this.generation === generation) { this.setLoading(false); this.availability(false); } /* Keep the external link on failure. */ }
      finally { if (this.generation === generation) this.loading = false; }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.generation++;
      if (window.cancelIdleCallback) cancelIdleCallback(this.backgroundTask);
      clearTimeout(this.backgroundTask);
      this.backgroundTask = null;
      this.backgroundScheduled = false;
      this.observer?.disconnect();
      this.resetWidget();
      this.mounted = this.loading = false;
      this.querySelector('[data-review-fallback]').hidden = false;
    }

    availability(available) {
      document.dispatchEvent(new CustomEvent('reviews:availability', { detail: { productId: this.dataset.productId, available } }));
    }

    resetWidget() {
      this.dispose?.();
      this.dispose = null;
      this.mounted = false;
      delete this.dataset.loaded;
      this.setLoading(false);
      this.querySelector('[data-review-placeholder]').hidden = false;
      const host = this.querySelector('[data-review-host]');
      if (host?.id) {
        const replacement = host.cloneNode(false);
        replacement.removeAttribute('id');
        host.replaceWith(replacement);
      }
    }

    setLoading(loading) {
      this.setAttribute('aria-busy', String(loading));
      this.querySelector('[data-review-loading]').hidden = !loading;
      this.querySelector('[data-review-skeleton]').hidden = !loading;
    }
  }
  customElements.define('product-review-summary', ProductReviewSummary);
  customElements.define('product-review-widget', ProductReviewWidget);
})();

(() => {
  if (!window.customElements || customElements.get('collection-card-rating')) return;
  class CollectionCardRating extends HTMLElement {
    connectedCallback() {
      const generation = this.generation = (this.generation || 0) + 1;
      const load = async () => {
        this.observer?.disconnect();
        let skus;
        try { skus = JSON.parse(this.dataset.skus); } catch (_) { return; }
        if (!Array.isArray(skus)) return;
        const value = await window.TFFCProductReviews.productStats(skus);
        if (!this.isConnected || generation !== this.generation || !window.TFFCProductReviews.qualifies(value)) return;
        this.querySelector('[data-card-score]').textContent = value.rating.toFixed(1);
        this.querySelector('[data-card-count]').textContent = `(${value.count})`;
        this.querySelector('[data-card-stars]').style.setProperty('--rating-fill', `${value.rating * 20}%`);
        this.querySelector('a').setAttribute('aria-label', this.dataset.label.replace('__rating__', value.rating.toFixed(1)).replace('__count__', value.count));
        this.querySelector('a').hidden = false;
      };
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) load(); }, { rootMargin: '200px' });
        this.observer.observe(this.closest('product-item') || this);
      } else load();
    }
    disconnectedCallback() { this.generation++; this.observer?.disconnect(); }
  }
  customElements.define('collection-card-rating', CollectionCardRating);
})();
